<template>
	<div class="page">
		<Navigation pageName="scriptsdeployhistory" />

		<form class="filter-section" @submit.prevent>
			<div class="filter-left">
				<label class="text-bold">{{ $t("Search") }}</label>
				<input v-model.trim="searchTerm" class="input pl-2 select text-small" type="text" :placeholder="$t('Search')" />
			</div>
			<div class="filter-left">
				<label class="text-bold">{{ $t("Script") }}</label>
				<select v-model="selectedScriptId" class="select text-small">
					<option value="">{{ $t('All scripts') }}</option>
					<option v-for="script in availableScripts" :key="script.id" :value="script.id">
						{{ script.name }}
					</option>
				</select>
			</div>
		</form>

		<div class="results-section">
			<table class="table border striped m-0 bg-white">
				<thead>
					<tr>
						<th>{{ $t("Created at") }}</th>
						<th>{{ $t("Name") }}</th>
						<th>{{ $t("Script") }}</th>
						<th>{{ $t("User") }}</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					<tr v-if="loading">
						<td colspan="5" class="text-center p-5">
							<span class="mif-spinner2 fg-primary ani-spin mif-3x" />
						</td>
					</tr>
					<tr v-else-if="filteredDeployHistory.length === 0">
						<td colspan="5">
							<NoDataAvailable :message="$t('No deployment history available')" />
						</td>
					</tr>
					<template v-else>
						<tr v-for="deployment in filteredDeployHistory" :key="deployment.id">
							<td>
								{{ formatDate(deployment.created_at) }}
							</td>
							<td>{{ deployment.name }}</td>
							<td>
								<a v-if="scriptsMap[deployment.id_script]" class="text-bold button flat-button p-0" @click="openDialogScript(deployment.id_script)">
									{{ scriptsMap[deployment.id_script].name }}
								</a>
								<span v-else>{{ deployment.id_script }}</span>
							</td>
							<td class="no-wrap">
								{{ $root.userDisplayName(deployment.id_user) }}
							</td>

							<td class="no-wrap text-right">

								<button class="button flat-button" :title="$t('View')" @click="openDeployDetails(deployment)">
									<span class="mif-2x mif-eye"></span>
								</button>
								<button class="button flat-button" :title="isDraftScript(deployment.id_script) ? $t('Draft scripts cannot be deployed') : $t('Repeat deploy')" :disabled="repeatingDeployId === deployment.id || isDraftScript(deployment.id_script)" @click="repeatDeploy(deployment)">
									<span :class="repeatingDeployId === deployment.id ? 'mif-spinner2 ani-spin' : 'mif-2x mif-loop2'"></span>
								</button>
								<button class="button flat-button fg-red" :title="$t('Delete')" :disabled="deletingDeployId === deployment.id" @click="deleteDeploy(deployment)">
									<span :class="deletingDeployId === deployment.id ? 'mif-spinner2 ani-spin' : 'mif-2x mif-cross'"></span>
								</button>
							</td>
						</tr>
					</template>
				</tbody>
			</table>
		</div>
	</div>
</template>

<script>
import Navigation from '../components/utils/Navigation';
import NoDataAvailable from '../components/utils/NoDataAvailable.vue';

export default {
	name: 'ScriptDeployHistory',
	components: {
		Navigation,
		NoDataAvailable
	},
	data() {
		return {
			deployHistory: null,
			scripts: [],
			scriptsMap: null,
			searchTerm: '',
			selectedScriptId: '',
			repeatingDeployId: null,
			deletingDeployId: null,
			loading: true
		};
	},
	computed: {
		availableScripts() {
			return [...this.scripts].sort((first, second) =>
				String(first.name || '').localeCompare(String(second.name || ''), undefined, {
					sensitivity: 'base',
					numeric: true
				})
			);
		},
		filteredDeployHistory() {
			let history = [...(this.deployHistory || [])].sort((first, second) =>
				this.dateTimestamp(second.created_at) - this.dateTimestamp(first.created_at)
			);
			if (this.selectedScriptId) {
				history = history.filter(deployment => deployment.id_script === this.selectedScriptId);
			}
			if (!this.searchTerm) return history;

			const term = this.searchTerm.toLowerCase();
			return history.filter(deployment => [
				deployment.id,
				deployment.name,
				deployment.id_script,
				this.scriptsMap[deployment.id_script] && this.scriptsMap[deployment.id_script].name,
				deployment.id_user,
				this.$root.userDisplayName(deployment.id_user)
			].some(value => String(value || '').toLowerCase().includes(term)));
		},

	},
	mounted() {
		this.$session.setRoomPC(null);
		this.$root.socket('SETPC', null);
		this.$root.$on('REFRESHSCRIPTDEPLOYHISTORY', this.loadData);
		this.loadData();
	},
	beforeDestroy() {
		this.$root.$off('REFRESHSCRIPTDEPLOYHISTORY', this.loadData);
	},
	methods: {
		async loadData() {
			this.loading = true;
			await this.$root.ensureUsefulMaps()
			try {
				const [deployHistory, scripts] = await Promise.all([
					this.$api.getScriptDeployHistory(),
					this.$api.getScripts()
				]);
				this.deployHistory = deployHistory;
				this.scripts = scripts;
				this.scriptsMap = this.scripts.toMap("id");
			} finally {
				this.loading = false;
			}
		},

		openDialogScript(idScript) {
			const script = this.scriptsMap && this.scriptsMap[idScript];
			if (script) {
				this.$router.push({ name: 'script', params: { idScript: String(idScript) } });
			}
		},
		openDeployDetails(deployment) {
			this.$root.$emit('OPENSCRIPTDEPLOYDIALOG', deployment);
		},
		async repeatDeploy(deployment) {
			if (this.repeatingDeployId != null || this.isDraftScript(deployment.id_script)) return;
			this.repeatingDeployId = deployment.id;
			try {
				const response = await this.$api.getScriptDeployResults(deployment.id);
				const computerIds = [...new Set((response && response.computer_ids) || [])];

				this.$root.$emit('OPENSCRIPTDEPLOYDIALOG', {
					deploy: {
						id_script: deployment.id_script,
						name: deployment.name
					},
					computer_ids: computerIds
				});
			} finally {
				this.repeatingDeployId = null;
			}
		},
		isDraftScript(idScript) {
			const script = this.scriptsMap && this.scriptsMap[idScript];
			return !!script && script.status === 'draft';
		},
		deleteDeploy(deployment) {
			const dialogID = `delete-deploy-${deployment.id}`;
			this.$root.$once(dialogID, async confirmed => {
				if (!confirmed) return;
				this.deletingDeployId = deployment.id;
				try {
					const deleted = await this.$api.deleteScriptDeploy(deployment.id);
					if (deleted) {
						this.deployHistory = this.deployHistory.filter(item => item.id !== deployment.id);
					}
				} finally {
					this.deletingDeployId = null;
				}
			});
			this.$root.openYesNoDialog(
				dialogID,
				this.$t('Delete deploy'),
				this.$t('Are you sure you want to delete this deploy?')
			);
		},
		dateTimestamp(value) {
			const timestamp = value ? new Date(value).getTime() : 0;
			return Number.isNaN(timestamp) ? 0 : timestamp;
		},
		formatDate(value) {
			if (!value) {
				return '';
			}
			return typeof value.toLocalDate === 'function'
				? value.toLocalDate()
				: new Date(value).toLocaleString();
		}
	}
};
</script>
